/**
 * A custom web component that displays a bubble-like UI element
 * with open/close functionality and accessibility features.
 */
class SkrBubble extends HTMLElement {
    /** @type {boolean} Tracks whether the bubble is open or not */
    _isOpen;

    /** @type {HTMLButtonElement | null} Reference to the close button */
    closeButton;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this._isOpen = false;

        this.shadowRoot.innerHTML = `      
          <slot></slot>
          
          <button class="bubble__open-button" aria-label="Open" part="open-button">
            <i class="bubble__open-button-icon" part="open-button-icon"></i>
          </button>
          <button class="bubble__close-button" aria-label="Close" part="close-button"></button>
        `;
    }

    /**
     * Lifecycle method called when the component is added to the DOM.
     * Sets up event listeners.
     */
    connectedCallback() {
        this.closeButton = this.shadowRoot.querySelector('.bubble__close-button');

        this.closeButton?.addEventListener('click', (e) => {
            e.stopPropagation();
            this._closeBubble();
        });

        this.addEventListener('click', () => {
            if (!this._isOpen) {
                this._openBubble();
            }
        });
    }

    /**
     * Lifecycle method called when the component is removed from the DOM.
     * Cleans up global event listeners.
     */
    disconnectedCallback() {
        document.removeEventListener('keydown', this._onKeyDown);
    }

    /**
     * Closes the bubble and updates UI state.
     * @private
     */

    _closeBubble = () => {
        this._isOpen = false;
        this._updateUI();
    }

    /**
     * Opens the bubble and updates UI state.
     * @private
     */
    _openBubble = () => {
        this._isOpen = true;
        this._updateUI();
    }

    /**
     * Handles Escape key press to close the bubble.
     * @param {KeyboardEvent} e
     * @private
     */
    _onKeyDown = (e) => {
        if (e.key === 'Escape' && this._isOpen) {
            this._closeBubble();
        }
    };

    /**
     * Updates the UI and accessibility attributes based on current state.
     * Adds/removes event listeners and sets `aria-expanded`.
     * @private
     */
    _updateUI = () => {
        this._isOpen
            ? document.addEventListener('keydown', this._onKeyDown)
            : document.removeEventListener('keydown', this._onKeyDown);

        this.setAttribute('aria-expanded', this._isOpen ? 'true' : 'false');
    }
}

// Register the custom element
customElements.define('skr-bubble', SkrBubble);
